import { useEffect } from 'react';
import { Field } from './components/Field';
import { Nav } from './components/Nav';
import { ContactForm } from './components/ContactForm';
import { prefersReducedMotion, scrollToId, setLenis } from './lib/scroll';
import {
  ACTS,
  contact,
  edge,
  hero,
  human,
  practice,
  record,
  site,
  thesis,
  ventures,
  type Venture,
} from './content';

/* ------------------------------------------------------------- primitives */

function Section({
  id,
  act,
  className,
  children,
}: {
  id: string;
  act: number;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <section id={id} data-act={act} className={`section${className ? ` ${className}` : ''}`}>
      <div className="wrap">{children}</div>
    </section>
  );
}

function ActHead({ title, readout }: { title: string; readout?: string }) {
  return (
    <div className="act-head reveal">
      <h2 className="h2">{title}</h2>
      {readout ? <span className="mono act-readout">{readout}</span> : null}
    </div>
  );
}

function DataStrip({ items }: { items: readonly { k: string; v: string }[] }) {
  return (
    <dl className="data">
      {items.map((it) => (
        <div key={it.k}>
          <dt>{it.k}</dt>
          <dd>{it.v}</dd>
        </div>
      ))}
    </dl>
  );
}

function Arrow() {
  return (
    <span className="arrow" aria-hidden="true">
      →
    </span>
  );
}

/* ---------------------------------------------------------------- sections */

function Hero() {
  return (
    <section id="top" data-act={ACTS.hero} className="section hero">
      <div className="wrap">
        <div className="grid">
          <div style={{ gridColumn: '1 / 9' }}>
            <div className="hero-mast reveal">
              <span className="name">{site.name}</span>
              <span className="micro">{site.role}</span>
            </div>

            <h1 className="display reveal" style={{ ['--reveal-delay' as string]: '80ms' }}>
              {hero.headline.pre}
              <em>{hero.headline.em}</em>
            </h1>

            <p
              className="lead hero-lead reveal"
              style={{ ['--reveal-delay' as string]: '160ms' }}
            >
              {hero.lead}
            </p>

            <div className="hero-actions reveal" style={{ ['--reveal-delay' as string]: '240ms' }}>
              <a
                className="btn"
                href="#contact"
                onClick={(e) => {
                  if (e.metaKey || e.ctrlKey) return;
                  e.preventDefault();
                  scrollToId('contact');
                }}
              >
                Start a conversation
                <Arrow />
              </a>
              <a
                className="link link-mono"
                href="#bloom"
                onClick={(e) => {
                  if (e.metaKey || e.ctrlKey) return;
                  e.preventDefault();
                  scrollToId('bloom');
                }}
              >
                See the work
                <Arrow />
              </a>
            </div>

            <dl className="hero-readouts mono reveal" style={{ ['--reveal-delay' as string]: '320ms' }}>
              {hero.readouts.map((r) => (
                <div key={r.k}>
                  <dt>{r.k}</dt>
                  <dd>{r.v}</dd>
                </div>
              ))}
            </dl>
          </div>
        </div>
      </div>
    </section>
  );
}

function Thesis() {
  return (
    <Section id="thesis" act={ACTS.thesis}>
      <div className="grid">
        <div style={{ gridColumn: '1 / 8' }}>
          <ActHead title={thesis.heading} />
          <div className="prose measure reveal">
            {thesis.body.map((p) => (
              <p key={p.slice(0, 24)}>{p}</p>
            ))}
          </div>
          <p className="thesis-quote reveal">
            Most systems are not life-critical.{' '}
            <strong>The ones worth hiring me for behave as if they are.</strong>
          </p>
        </div>
      </div>
    </Section>
  );
}

function VentureBlock({ v, side }: { v: Venture; side: 'left' | 'right' }) {
  const col = side === 'left' ? '1 / 8' : '6 / 13';
  return (
    <Section id={v.id} act={ACTS[v.id as keyof typeof ACTS]} className="venture">
      <div className="grid">
        <div style={{ gridColumn: col }}>
          <div className="venture-head reveal">
            <h2>{v.name}</h2>
            <span className="mono">{v.years}</span>
            <span className="micro">{v.domain}</span>
          </div>
          <p className="venture-tagline reveal" style={{ ['--reveal-delay' as string]: '60ms' }}>
            {v.tagline}
          </p>
          <div className="prose measure reveal" style={{ ['--reveal-delay' as string]: '120ms' }}>
            {v.body.map((p) => (
              <p key={p.slice(0, 24)}>{p}</p>
            ))}
          </div>
          <div className="reveal" style={{ ['--reveal-delay' as string]: '180ms', marginTop: 'clamp(1.75rem, 4vh, 2.5rem)' }}>
            <DataStrip items={v.specs} />
          </div>
          {v.link ? (
            <div className="venture-foot reveal">
              <a className="link link-mono" href={v.link.href} rel="noreferrer noopener" target="_blank">
                {v.link.label}
                <Arrow />
              </a>
            </div>
          ) : null}
        </div>
      </div>
    </Section>
  );
}

function Edge() {
  return (
    <Section id="edge" act={ACTS.edge}>
      <div className="grid">
        <div style={{ gridColumn: '1 / 9' }}>
          <ActHead title={edge.heading} readout="Edge · Rust · embedded" />
          <div className="prose measure reveal">
            {edge.body.map((p) => (
              <p key={p.slice(0, 24)}>{p}</p>
            ))}
          </div>
        </div>
        <div style={{ gridColumn: '1 / 9', marginTop: 'clamp(2.25rem, 5vh, 3.25rem)' }}>
          <div className="reveal">
            <DataStrip items={edge.specs} />
          </div>
          <div className="venture-foot reveal">
            <a className="link link-mono" href={site.github} rel="noreferrer noopener" target="_blank">
              github.com/danzlarkin
              <Arrow />
            </a>
          </div>
        </div>
      </div>
    </Section>
  );
}

function Practice() {
  return (
    <Section id="practice" act={ACTS.practice}>
      <div className="grid">
        <div style={{ gridColumn: '1 / 13' }}>
          <ActHead title={practice.heading} readout="Advisory · Build · Diligence" />
        </div>
        <div style={{ gridColumn: '1 / 13' }}>
          <div className="engagements">
            {practice.engagements.map((e, i) => (
              <div
                className="engagement reveal"
                key={e.n}
                style={{ ['--reveal-delay' as string]: `${i * 90}ms` }}
              >
                <span className="engagement-n">{e.n}</span>
                <div>
                  <h3>{e.title}</h3>
                  <p className="engagement-hook">{e.hook}</p>
                </div>
                <p className="engagement-body">{e.body}</p>
                <span className="engagement-shape">{e.shape}</span>
              </div>
            ))}
          </div>
          <ul className="domains reveal">
            {practice.domains.map((d) => (
              <li key={d}>{d}</li>
            ))}
          </ul>
        </div>
      </div>
    </Section>
  );
}

function Record() {
  return (
    <Section id="record" act={ACTS.record} className="section--tight">
      <div className="grid">
        <div style={{ gridColumn: '1 / 13' }}>
          <ActHead title={record.heading} readout="Tecto · New Zealand" />
          <p className="lead measure reveal record-lead">{record.lead}</p>
          <div className="record reveal">
            {record.items.map((it) => (
              <div key={it.v}>
                <span className="record-v">{it.v}</span>
                <span className="record-k">{it.k}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </Section>
  );
}

function Human() {
  return (
    <Section id="human" act={ACTS.human} className="section--tight">
      <div className="grid">
        <div style={{ gridColumn: '1 / 5' }}>
          <figure className="human-figure reveal">
            <picture>
              <source
                type="image/avif"
                srcSet="/img/daniel-240.avif 240w, /img/daniel-460.avif 460w"
                sizes="(max-width: 899px) 240px, 300px"
              />
              <source
                type="image/webp"
                srcSet="/img/daniel-240.webp 240w, /img/daniel-460.webp 460w"
                sizes="(max-width: 899px) 240px, 300px"
              />
              <img
                src="/img/daniel-460.jpg"
                srcSet="/img/daniel-240.jpg 240w, /img/daniel-460.jpg 460w"
                sizes="(max-width: 899px) 240px, 300px"
                width={300}
                height={300}
                loading="lazy"
                decoding="async"
                alt={human.photoAlt}
              />
            </picture>
            <figcaption>
              {site.location} · {site.timezone}
            </figcaption>
          </figure>
        </div>
        <div style={{ gridColumn: '6 / 13' }}>
          <ActHead title={human.heading} />
          <div className="prose measure reveal">
            {human.body.map((p) => (
              <p key={p.slice(0, 24)}>{p}</p>
            ))}
          </div>
        </div>
      </div>
    </Section>
  );
}

function Contact() {
  return (
    <Section id="contact" act={ACTS.contact}>
      <div className="grid">
        <div style={{ gridColumn: '1 / 13' }}>
          <ActHead title={contact.heading} />
        </div>
        <div style={{ gridColumn: '1 / 8' }}>
          <p className="lead measure reveal" style={{ marginBottom: 'clamp(1.75rem, 4vh, 2.5rem)' }}>
            {contact.lead}
          </p>
          <div className="reveal" style={{ ['--reveal-delay' as string]: '80ms' }}>
            <ContactForm />
          </div>
        </div>
        <div style={{ gridColumn: '9 / 13' }}>
          <div className="reveal" style={{ ['--reveal-delay' as string]: '160ms' }}>
            <h3 className="next-head">What happens next</h3>
            <ol className="next">
              {contact.next.map((n) => (
                <li key={n.k}>
                  <span className="next-k">{n.k}</span>
                  <span className="next-v">{n.v}</span>
                </li>
              ))}
            </ol>
            <div className="contact-direct">
              <p className="micro">Or go direct</p>
              <a href={`mailto:${site.email}`}>{site.email}</a>
              <a href={site.github} rel="me noreferrer noopener" target="_blank">
                github.com/danzlarkin
              </a>
              <a href={site.medium} rel="me noreferrer noopener" target="_blank">
                medium.com/@danzlarkin
              </a>
            </div>
          </div>
        </div>
      </div>
    </Section>
  );
}

function Footer() {
  return (
    <footer className="footer">
      <div className="wrap">
        {/* Server and client can disagree on the year for a few hours around
            New Year; the mismatch is not worth a hydration warning. */}
        <p className="footer-note" suppressHydrationWarning>
          © {new Date().getFullYear()} {site.name}
        </p>
      </div>
    </footer>
  );
}

/* -------------------------------------------------------------------- app */

export default function App() {
  useEffect(() => {
    document.documentElement.classList.add('js');

    const reduced = prefersReducedMotion();

    // Reveals: content is visible by default; this only plays the entrance.
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) {
            (e.target as HTMLElement).dataset.seen = 'true';
            io.unobserve(e.target);
          }
        }
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.08 },
    );
    if (!reduced) {
      for (const el of document.querySelectorAll('.reveal')) io.observe(el);
    } else {
      for (const el of document.querySelectorAll<HTMLElement>('.reveal')) {
        el.dataset.seen = 'true';
      }
    }

    let cancelled = false;
    let stopSmooth: (() => void) | undefined;
    if (!reduced) {
      void import('lenis').then(({ default: Lenis }) => {
        if (cancelled) return;
        const lenis = new Lenis({
          duration: 1.05,
          easing: (t: number) => 1 - Math.pow(1 - t, 4),
          wheelMultiplier: 1,
          touchMultiplier: 1.6,
          autoRaf: true,
        });
        setLenis(lenis);
        document.documentElement.style.scrollBehavior = 'auto';
        stopSmooth = () => {
          setLenis(null);
          lenis.destroy();
          document.documentElement.style.removeProperty('scroll-behavior');
        };
      });
    }

    // Deep links still land in the right place once Lenis is driving.
    if (location.hash.length > 1) {
      const id = location.hash.slice(1);
      requestAnimationFrame(() => scrollToId(id));
    }

    return () => {
      cancelled = true;
      io.disconnect();
      stopSmooth?.();
    };
  }, []);

  return (
    <>
      <a className="skip" href="#thesis">
        Skip to content
      </a>
      <Field />
      <Nav />
      <div className="shell">
        <main id="main">
          <Hero />
          <Thesis />
          {ventures.map((v, i) => (
            <VentureBlock key={v.id} v={v} side={i % 2 === 0 ? 'right' : 'left'} />
          ))}
          <Edge />
          <Practice />
          <Record />
          <Human />
          <Contact />
        </main>
        <Footer />
      </div>
    </>
  );
}
